import { Reducer } from 'redux';
import { types } from './internationalization.types';
import { IInternationalizationState, ILanguage, ITranslation, LANGUAGES_CODES } from './internationalization';
import queryString from 'querystring';
import _ from 'lodash';
import { parseLang } from 'utils';
import { flattenObject } from 'utils/object.util';

const initialState: IInternationalizationState = {
  isLoading: false,
  error: null,
  languages: [],
  selectedLanguage: null!,
  translations: null!,
  termsAndConditions: null,
  gdprPrivacyPolicy: null,
  customerPrivacyPolicy: null,
};

const getLanguage = (languageCode: string | string[], languages: ILanguage[]) : ILanguage | undefined => {
  if (!languageCode || languageCode === '') return undefined;
  return _.find(languages, (language: ILanguage) => language.code === languageCode);
};

const internationalizationReducer: Reducer<IInternationalizationState, any> = (state = initialState, action): IInternationalizationState => {
  switch (action.type) {
    case types.GET_LANGUAGES_STARTED: {
      return { ...state, isLoading: true };
    }

    case types.GET_LANGUAGES_FAILURE: {
      return { ...state, isLoading: false, error: action.payload as string };
    }

    case types.GET_LANGUAGES_SUCCESS: {
      const languages = action.payload as ILanguage[];
      if (!languages?.length) throw new Error('No languages found!');

      const query = queryString.parse(window.location.search.substring(1));
      const lang = parseLang(query.lang) ?? '';

      const selectedLanguage =
        getLanguage(lang, languages) ??
        getLanguage(LANGUAGES_CODES.ENGLISH, languages) ??
        languages[0];

      return { ...state, isLoading: false, error: null, languages, selectedLanguage };
    }

    case types.GET_SELECTED_LANGUAGE_SUCCESS: {
      return {
        ...state,
        selectedLanguage: action.payload as ILanguage,
        termsAndConditions: null,
        gdprPrivacyPolicy: null,
        customerPrivacyPolicy: null
      };
    }
    case types.GET_TRANSLATIONS_STARTED: {
      return { ...state, isLoading: true };
    }

    case types.GET_TRANSLATIONS_FAILURE: {
      return { ...state, isLoading: false, error: action.payload as string };
    }

    case types.GET_TRANSLATIONS_SUCCESS: {
      const translations: Record<string, any> = {};
      const payload: ITranslation[] = action.payload;
      const payloadLength = payload.length;

      if (payloadLength) {
        payload.forEach((translation) => {
          const key = translation.customImageTypeId !== null
            ? translation.customImageTypeId
            : translation.categoryName;

          translations[key] = (() => {
            const values: Record<string, any> = {};

            translation.translationValues.forEach(({ key, value }) => {
              values[key] = value;
            });
            return values;
          })();
        });
      }

      return { ...state, isLoading: false, error: null, translations: flattenObject(translations) };
    }

    case types.GET_TERMS_AND_CONDITIONS_STARTED: {
      return { ...state, isLoading: true };
    }

    case types.GET_TERMS_AND_CONDITIONS_FAILURE: {
      return { ...state, isLoading: false, error: action.payload as string };
    }

    case types.GET_TERMS_AND_CONDITIONS_SUCCESS: {
      return { ...state, isLoading: false, error: null, termsAndConditions: action.payload };
    }

    case types.GET_GDPR_PRIVACY_POLICY_STARTED: {
      return { ...state, isLoading: true };
    }

    case types.GET_GDPR_PRIVACY_POLICY_FAILURE: {
      return { ...state, isLoading: false, error: action.payload as string };
    }

    case types.GET_GDPR_PRIVACY_POLICY_SUCCESS: {
      return { ...state, isLoading: false, error: null, gdprPrivacyPolicy: action.payload };
    }

    case types.GET_CUSTOMER_PRIVACY_POLICY_STARTED: {
      return { ...state, isLoading: true };
    }

    case types.GET_CUSTOMER_PRIVACY_POLICY_FAILURE: {
      return { ...state, isLoading: false, error: action.payload as string };
    }

    case types.GET_CUSTOMER_PRIVACY_POLICY_SUCCESS: {
      return { ...state, isLoading: false, error: null, customerPrivacyPolicy: action.payload };
    }

    default: {
      return state;
    }
  }
};

export { internationalizationReducer };
